import * as pdfjs from 'pdfjs-dist';

// Configure PDF.js worker - use unpkg CDN for the worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// CORS proxy function - use a public CORS proxy
const createProxiedUrl = (url) => {
  // Use a public CORS proxy
  return `https://corsproxy.io/?${encodeURIComponent(url)}`;
};

export const extractPDFArea = async (pdfUrl, x, y, width, height, scale = 2) => {
  try {
    // Load the PDF document through CORS proxy
    const proxiedUrl = createProxiedUrl(pdfUrl);
    console.log('Loading PDF through proxy:', proxiedUrl);
    const loadingTask = pdfjs.getDocument(proxiedUrl);
    const pdf = await loadingTask.promise;
    
    // Get the first page (for now, we'll assume single page or first page)
    const page = await pdf.getPage(1);
    
    // Get page dimensions
    const viewport = page.getViewport({ scale });
    
    // Create a canvas for rendering the full page at high resolution
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    
    // Render the PDF page to canvas
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };
    
    await page.render(renderContext).promise;
    
    // Create a new canvas for the extracted area
    const extractedCanvas = document.createElement('canvas');
    const extractedContext = extractedCanvas.getContext('2d');
    
    // Scale the selection coordinates to match the high-resolution rendering
    const scaledX = x * scale;
    const scaledY = y * scale;
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;
    
    extractedCanvas.width = scaledWidth;
    extractedCanvas.height = scaledHeight;
    
    // Extract the selected area from the full page canvas
    extractedContext.drawImage(
      canvas,
      scaledX, scaledY, scaledWidth, scaledHeight, // Source rectangle
      0, 0, scaledWidth, scaledHeight // Destination rectangle
    );
    
    // Convert to maximum quality PNG (lossless)
    return extractedCanvas.toDataURL('image/png', 1.0);
    
  } catch (error) {
    console.error('Error extracting PDF area:', error);
    return null;
  }
};

export const getPDFPageCount = async (pdfUrl) => {
  try {
    let documentUrl;
    if (pdfUrl.startsWith('blob:') || pdfUrl.includes('localhost') || pdfUrl.includes('127.0.0.1')) {
      // Local file or localhost - use directly without proxy
      documentUrl = pdfUrl;
    } else {
      // Remote URL - use proxy
      documentUrl = createProxiedUrl(pdfUrl);
    }
    const loadingTask = pdfjs.getDocument(documentUrl);
    const pdf = await loadingTask.promise;
    return pdf.numPages;
  } catch (error) {
    console.error('Error getting PDF page count:', error);
    return 1;
  }
};

export const extractPDFAreaFromPage = async (pdfUrl, pageNumber, x, y, width, height, displayScale = 1, extractionScale = 2, rotation = 0) => {
  try {
    // Check if pdfUrl is a blob URL (local file), localhost, or a regular remote URL
    let documentUrl;
    if (pdfUrl.startsWith('blob:') || pdfUrl.includes('localhost') || pdfUrl.includes('127.0.0.1')) {
      // Local file or localhost - use directly without proxy
      documentUrl = pdfUrl;
      console.log('Extracting PDF area from local/localhost file:', { pdfUrl, pageNumber, x, y, width, height, displayScale, extractionScale, rotation });
    } else {
      // Remote URL - use proxy
      documentUrl = createProxiedUrl(pdfUrl);
      console.log('Extracting PDF area from remote URL through proxy:', { documentUrl, pageNumber, x, y, width, height, displayScale, extractionScale, rotation });
    }
    
    const loadingTask = pdfjs.getDocument(documentUrl);
    const pdf = await loadingTask.promise;
    
    // Get the specified page
    const page = await pdf.getPage(pageNumber);
    
    // Get page dimensions using the extraction scale for high resolution
    const viewport = page.getViewport({ scale: extractionScale, rotation });
    
    // Create a canvas for rendering the page at high resolution
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    
    // Render the PDF page to canvas
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };
    
    await page.render(renderContext).promise;
    
    // Create a new canvas for the extracted area
    const extractedCanvas = document.createElement('canvas');
    const extractedContext = extractedCanvas.getContext('2d');
    
    // Convert display coordinates to extraction coordinates
    // The coordinates come from the display canvas (at displayScale)
    // We need to map them to the extraction canvas (at extractionScale)
    const scaleRatio = extractionScale / displayScale;
    const scaledX = x * scaleRatio;
    const scaledY = y * scaleRatio;
    const scaledWidth = width * scaleRatio;
    const scaledHeight = height * scaleRatio;
    
    console.log('High-resolution extraction:', {
      input: { x, y, width, height },
      displayScale,
      extractionScale,
      scaleRatio,
      output: { scaledX, scaledY, scaledWidth, scaledHeight },
      extractionCanvasSize: { width: viewport.width, height: viewport.height },
      finalImageSize: { width: scaledWidth, height: scaledHeight },
      qualityMultiplier: `${extractionScale}x original PDF resolution`
    });
    
    extractedCanvas.width = scaledWidth;
    extractedCanvas.height = scaledHeight;
    
    // Extract the selected area from the full page canvas
    extractedContext.drawImage(
      canvas,
      scaledX, scaledY, scaledWidth, scaledHeight, // Source rectangle
      0, 0, scaledWidth, scaledHeight // Destination rectangle
    );
    
    // Convert to maximum quality PNG (lossless)
    return extractedCanvas.toDataURL('image/png', 1.0);
    
  } catch (error) {
    console.error('Error extracting PDF area from page:', error);
    return null;
  }
};